import React, { useEffect, useRef, useState } from "react";
import { Box, Text, Loader, createStyles, keyframes } from "@mantine/core";
import { IconSparkles, IconCheck, IconSend } from "@tabler/icons-react";
import { TARA } from "../../pages/Home/QuarterlyReportsPage/theme";
import { askTaraAi } from "../../services/quarterlyReport";

// Prototype's .skai-panel geometry: the drawer is 620 wide and the panel sits
// 14px to its left, 16px off the top/bottom. The prototype's drawer is inset
// 16px from the right edge; Mantine's Drawer is flush, so the offset here is
// 620 + 14 rather than 16 + 620 + 14.
const DRAWER_WIDTH = 620;
const PANEL_GAP = 14;

const taraPulse = keyframes({
  "0%, 100%": { boxShadow: "0 0 0 0 rgba(120,86,255,.45)" },
  "50%": { boxShadow: "0 0 0 10px rgba(120,86,255,0)" },
});
const taraSpin = keyframes({ from: { transform: "rotate(0)" }, to: { transform: "rotate(360deg)" } });

const useStyles = createStyles(() => ({
  panel: {
    position: "fixed",
    top: 16,
    bottom: 16,
    right: DRAWER_WIDTH + PANEL_GAP,
    width: 440,
    background: TARA.bg,
    border: `1px solid ${TARA.border}`,
    borderRadius: 16,
    boxShadow: "-20px 0 48px rgba(15,23,42,.12)",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 0,
    padding: 24,
    overflowY: "auto",
    // Higher than the Generate drawer (zIndex 1000) it sits beside, so the
    // panel never renders underneath the drawer or its overlay.
    zIndex: 1050,
  },

  // ---- avatar (.skai-avatar-wrap / .skai-icon / .skai-badge) ----
  avatarWrap: { position: "relative", margin: "2px auto 16px", alignSelf: "center" },
  avatar: {
    position: "relative",
    width: 84,
    height: 84,
    borderRadius: "50%",
    background: "#fff",
    border: `3px solid ${TARA.accent}`,
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    "& img": { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  },
  avatarPulse: {
    animation: `${taraPulse} 1.6s ease-in-out infinite`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  badge: {
    position: "absolute",
    left: 64,
    top: 76,
    width: 26,
    height: 26,
    borderRadius: 8,
    background: TARA.accent,
    color: "#fff",
    display: "grid",
    placeItems: "center",
    border: `2px solid ${TARA.bg}`,
  },

  // ---- header copy (.skai-eyebrow / .skai-title / .skai-sub) ----
  eyebrow: {
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: ".1em",
    textTransform: "uppercase",
    color: TARA.ink3,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 20,
    "&::before": {
      content: '""',
      width: 6,
      height: 6,
      background: TARA.accent,
      borderRadius: "50%",
      display: "inline-block",
    },
  },
  title: {
    margin: "0 0 8px",
    fontSize: 22,
    fontWeight: 700,
    color: TARA.ink,
    letterSpacing: "-0.018em",
    lineHeight: 1.22,
    fontFamily: "Inter",
  },
  sub: { margin: "0 0 20px", fontSize: 14, color: TARA.ink2, lineHeight: 1.55 },

  // ---- task list (.skai-task-list / .skai-task) ----
  taskList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    alignSelf: "stretch",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  task: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "11px 14px",
    background: "#fff",
    border: `1px solid ${TARA.borderSoft}`,
    borderRadius: 10,
    fontSize: 13.5,
    fontWeight: 600,
    color: TARA.ink4,
    transition: "all .18s ease",
  },
  taskIcon: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    background: "transparent",
    border: `1.5px dashed ${TARA.borderStrong}`,
    color: "transparent",
    transition: "all .18s ease",
    "& svg": { opacity: 0, transition: "opacity .15s ease" },
  },
  taskDone: { borderColor: TARA.border, color: TARA.ink2 },
  taskIconDone: {
    background: TARA.accent,
    borderColor: TARA.accent,
    color: "#fff",
    "& svg": { opacity: 1 },
  },
  taskActive: {
    borderColor: TARA.borderStrong,
    color: TARA.ink,
    boxShadow: "0 0 0 3px rgba(120,86,255,.12)",
  },
  taskIconActive: {
    background: TARA.accent,
    borderColor: TARA.accent,
    color: "#fff",
    animation: `${taraPulse} 1.4s ease-in-out infinite`,
    "& svg": { opacity: 1, animation: `${taraSpin} 1.6s linear infinite` },
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
      "& svg": { animation: "none" },
    },
  },

  // ---- chat card (.skai-chat) ----
  chat: {
    alignSelf: "stretch",
    marginTop: 16,
    padding: 14,
    background: "#fff",
    border: `1px solid ${TARA.border}`,
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  msgs: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
    maxHeight: 260,
    overflowY: "auto",
  },
  msg: {
    display: "inline-block",
    padding: "9px 12px",
    borderRadius: 12,
    fontSize: 13.5,
    lineHeight: 1.5,
    maxWidth: "100%",
  },
  msgBot: {
    alignSelf: "flex-start",
    background: "#F5F3FF",
    border: `1px solid ${TARA.borderSoft}`,
    color: TARA.ink,
    borderBottomLeftRadius: 4,
  },
  msgUser: {
    alignSelf: "flex-end",
    background: TARA.accent,
    color: "#fff",
    borderBottomRightRadius: 4,
  },
  form: { display: "flex", alignItems: "center", gap: 6 },
  chatInput: {
    flex: 1,
    minWidth: 0,
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${TARA.border}`,
    background: TARA.bg,
    fontSize: 13.5,
    color: TARA.ink,
    fontFamily: "inherit",
    "&::placeholder": { color: TARA.ink4 },
    "&:focus": {
      outline: "none",
      borderColor: TARA.accent,
      background: "#fff",
      boxShadow: "0 0 0 3px rgba(120,86,255,.15)",
    },
  },
  send: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: "none",
    background: TARA.accent,
    color: "#fff",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    cursor: "pointer",
    "&:hover:not(:disabled)": { background: "#6D45F0" },
    "&:disabled": { cursor: "default", opacity: 0.6 },
  },
}));

const TASKS = [
  "Reading approved notes for the period",
  "Matching notes to ISP outcomes and supports",
  "Drafting Sections A, B and C",
  "Cross-checking every claim against documentation",
];

const STATE_COPY = {
  setup: {
    title: "Tara Ai drafts each review from your notes",
    sub: "Every section is written only from the client's approved documentation for the period. Anything without evidence is left blank for you.",
    taskState: [null, null, null, null],
  },
  progress: (ctx) => ({
    title: `Tara Ai is drafting ${ctx.clientCount} reviews`,
    sub: "About a minute per client. You can close this panel — finished drafts appear in the report list.",
    taskState: ["done", "done", "active", null],
  }),
};

// Every reply below comes from POST /report/quarterly-pcr/tara-ai/ask, scoped
// server-side to this contractor's approved providers only (Story 3) — no
// canned copy, no data outside what that endpoint actually returns.
const TaraAssistantPanel = ({ state, clientCount, providerId, startDate, endDate, rows, batchId, style }) => {
  const { classes, cx } = useStyles();
  const copy = typeof STATE_COPY[state] === "function" ? STATE_COPY[state]({ clientCount }) : STATE_COPY[state];

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [asking, setAsking] = useState(false);
  const askedKeyRef = useRef(null);
  const explainedFailedRef = useRef(new Set());

  const context = { providerId, startDate, endDate, batchId };

  const ask = async (question, extraContext, { silent } = {}) => {
    // Snapshot the transcript BEFORE this turn — that's the prior conversation
    // the backend needs for multi-turn context (e.g. a follow-up question
    // about whichever client was just discussed); the current question is
    // sent separately.
    const history = messages;
    if (!silent) setMessages((m) => [...m, { kind: "user", text: question }]);
    setAsking(true);
    const reply = await askTaraAi(question, { ...context, ...extraContext }, history);
    setAsking(false);
    setMessages((m) => [...m, { kind: "bot", text: reply || "Sorry, I couldn't check that right now." }]);
  };

  // On entering setup with a provider + period selected, greet once per
  // provider/period combination instead of silently auto-running a check —
  // the user drives the conversation from here.
  useEffect(() => {
    if (state !== "setup" || !providerId || !startDate || !endDate) return;
    const key = `${providerId}|${startDate}|${endDate}`;
    if (askedKeyRef.current === key) return;
    askedKeyRef.current = key;
    setMessages([{ kind: "bot", text: "What would you like me to do today?" }]);
  }, [state, providerId, startDate, endDate]);

  // While generating, auto-explain any client that newly failed.
  useEffect(() => {
    if (state !== "progress" || !batchId) return;
    const newlyFailed = (rows || []).filter(
      (r) => r.status === "failed" && !explainedFailedRef.current.has(r.clientId)
    );
    newlyFailed.forEach((r) => {
      explainedFailedRef.current.add(r.clientId);
      ask(`Why did ${r.name}'s draft fail?`, { clientId: r.clientId }, { silent: false });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, batchId, rows]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = input.trim();
    if (!q || asking) return;
    setInput("");
    ask(q, {});
  };

  return (
    <Box className={classes.panel} style={style} aria-label="Tara Ai assistant">
      <Box className={classes.avatarWrap} aria-hidden="true">
        <Box className={cx(classes.avatar, state === "progress" && classes.avatarPulse)}>
          <img src="/assets/images/taraAi.png" alt="" />
        </Box>
        <Box className={classes.badge}>
          <IconSparkles size={13} />
        </Box>
      </Box>
      <Text className={classes.eyebrow}>Tara Ai assistant</Text>
      <Text className={classes.title}>{copy.title}</Text>
      <Text className={classes.sub}>{copy.sub}</Text>

      <ul className={classes.taskList} aria-live="polite">
        {TASKS.map((t, i) => (
          <li
            key={i}
            className={cx(
              classes.task,
              copy.taskState[i] === "done" && classes.taskDone,
              copy.taskState[i] === "active" && classes.taskActive
            )}
          >
            <span
              className={cx(
                classes.taskIcon,
                copy.taskState[i] === "done" && classes.taskIconDone,
                copy.taskState[i] === "active" && classes.taskIconActive
              )}
            >
              <IconCheck size={11} stroke={3} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      <Box className={classes.chat}>
        <Box className={classes.msgs}>
          {messages.map((m, i) => (
            <Box key={i} className={cx(classes.msg, m.kind === "bot" ? classes.msgBot : classes.msgUser)}>
              {m.text}
            </Box>
          ))}
          {asking && (
            <Box className={cx(classes.msg, classes.msgBot)}>
              <Loader size={12} color={TARA.accent} />
            </Box>
          )}
        </Box>
        <form className={classes.form} onSubmit={handleSubmit}>
          <input
            className={classes.chatInput}
            placeholder="Reply to Tara..."
            aria-label="Message Tara Ai"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <button type="submit" className={classes.send} aria-label="Send" disabled={asking || !input.trim()}>
            <IconSend size={14} />
          </button>
        </form>
      </Box>
    </Box>
  );
};

export default TaraAssistantPanel;
